/**
 * Multi-step renderer for a practice template. Each template has a
 * dedicated inline component below — the runner picks by template id
 * and persists responses + completion through /api/practice/sessions/:id.
 *
 * Templates are intentionally hand-coded (not driven by schema_json)
 * because each one wants distinct UX (forms, sliders, choice grids).
 * The schema_json column on the worker side is reserved for future
 * data-driven flows.
 */
import { useState } from "react";

import { useApi } from "@/lib/api";

interface Template {
  id: string;
  name: string;
  methodology: string;
}

export function PracticeRunner(props: { template: Template; onClose: () => void }) {
  const api = useApi();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [starting, setStarting] = useState(true);

  // Lazy-start: create the session row on first interaction inside the
  // template-specific component. We pre-create it here for simplicity.
  if (starting) {
    void (async () => {
      try {
        const r = await api<{ session: { id: string } }>("/api/practice/sessions", {
          method: "POST",
          body: JSON.stringify({ template_id: props.template.id }),
        });
        setSessionId(r.session.id);
      } finally {
        setStarting(false);
      }
    })();
  }

  if (!sessionId) {
    return <div className="py-4 text-center text-xs text-neutral-400">Starting…</div>;
  }

  const ctx: RunnerCtx = {
    sessionId,
    api,
    finish: async (responses: Record<string, unknown>) => {
      await api(`/api/practice/sessions/${sessionId}`, {
        method: "PATCH",
        body: JSON.stringify({ responses, completed: true }),
      });
    },
    close: props.onClose,
  };

  switch (props.template.id) {
    case "values_clarification":
      return <ValuesClarification ctx={ctx} />;
    case "tiny_habit_design":
      return <TinyHabitDesign ctx={ctx} />;
    case "thought_record":
      return <ThoughtRecord ctx={ctx} />;
    case "savoring":
      return <Savoring ctx={ctx} />;
    case "gratitude_three":
      return <GratitudeThree ctx={ctx} />;
    case "reframe_one":
      return <ReframeOne ctx={ctx} />;
    default:
      return <FallbackPractice ctx={ctx} />;
  }
}

interface RunnerCtx {
  sessionId: string;
  api: ReturnType<typeof useApi>;
  finish: (responses: Record<string, unknown>) => Promise<void>;
  close: () => void;
}

// ---- Shared layout primitives -----------------------------------------------

function RunnerShell(props: {
  title?: string;
  body: React.ReactNode;
  cta: React.ReactNode;
  ctaDisabled?: boolean;
  onCtaClick: () => void;
  ctaLabel?: string;
}) {
  return (
    <div className="space-y-3">
      {props.title && <p className="text-sm">{props.title}</p>}
      {props.body}
      <div className="flex justify-end pt-1">
        <button
          onClick={props.onCtaClick}
          disabled={props.ctaDisabled}
          className="rounded-2xl bg-neutral-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-30"
        >
          {props.ctaLabel ?? "Done"}
        </button>
      </div>
      {props.cta}
    </div>
  );
}

function CompleteScreen(props: { ctx: RunnerCtx; helpfulOnly?: boolean }) {
  const [picked, setPicked] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  async function rate(n: number) {
    setBusy(true);
    setPicked(n);
    try {
      await props.ctx.api(`/api/practice/sessions/${props.ctx.sessionId}`, {
        method: "PATCH",
        body: JSON.stringify({ helpful: n }),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="rounded-2xl bg-emerald-50 px-3 py-3 text-sm text-emerald-800">
        ✓ Saved. Streak counted.
      </div>
      <p className="text-sm text-neutral-700">Did this help?</p>
      <div className="grid grid-cols-3 gap-2">
        {[
          { n: 5, label: "👍 Yes" },
          { n: 3, label: "🤷 Meh" },
          { n: 1, label: "👎 No" },
        ].map((o) => (
          <button
            key={o.n}
            disabled={busy}
            onClick={() => rate(o.n)}
            className={
              "rounded-xl border px-3 py-2 text-sm " +
              (picked === o.n
                ? "border-neutral-900 bg-neutral-900 text-white"
                : "border-neutral-200 bg-white hover:border-neutral-400")
            }
          >
            {o.label}
          </button>
        ))}
      </div>
      <div className="flex justify-end">
        <button
          onClick={props.ctx.close}
          className="rounded-2xl border border-neutral-200 px-4 py-2 text-sm"
        >
          Close
        </button>
      </div>
    </div>
  );
}

// ---- ACT — values clarification ---------------------------------------------

const VALUES = [
  { id: "honesty", label: "Honesty", hint: "saying what's true" },
  { id: "creativity", label: "Creativity", hint: "making something new" },
  { id: "connection", label: "Connection", hint: "real closeness with people" },
  { id: "freedom", label: "Freedom", hint: "agency over my time + choices" },
  { id: "growth", label: "Growth", hint: "becoming more capable" },
  { id: "service", label: "Service", hint: "being useful to others" },
  { id: "playfulness", label: "Playfulness", hint: "lightness and fun" },
  { id: "courage", label: "Courage", hint: "moving toward fear" },
  { id: "stability", label: "Stability", hint: "predictability and calm" },
  { id: "craft", label: "Craft", hint: "doing things well" },
  { id: "wisdom", label: "Wisdom", hint: "seeing things clearly" },
  { id: "health", label: "Health", hint: "energy and vitality" },
];

function ValuesClarification({ ctx }: { ctx: RunnerCtx }) {
  const [picked, setPicked] = useState<string[]>([]);
  const [done, setDone] = useState(false);

  function toggle(id: string) {
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id].slice(-3)));
  }

  if (done) return <CompleteScreen ctx={ctx} />;

  return (
    <RunnerShell
      title="Pick three values that matter most to you right now. Not forever — right now."
      ctaLabel={picked.length === 3 ? "These three →" : `Pick 3 (${picked.length}/3)`}
      ctaDisabled={picked.length !== 3}
      onCtaClick={async () => {
        await ctx.finish({ values: picked });
        setDone(true);
      }}
      cta={null}
      body={
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {VALUES.map((v) => {
            const isPicked = picked.includes(v.id);
            return (
              <button
                key={v.id}
                onClick={() => toggle(v.id)}
                className={
                  "rounded-2xl border p-2 text-left text-sm transition " +
                  (isPicked
                    ? "border-neutral-900 bg-neutral-900 text-white"
                    : "border-neutral-200 bg-white hover:border-neutral-400")
                }
              >
                <div className="font-medium">{v.label}</div>
                <div className={"text-[11px] " + (isPicked ? "text-neutral-300" : "text-neutral-500")}>
                  {v.hint}
                </div>
              </button>
            );
          })}
        </div>
      }
    />
  );
}

// ---- Tiny Habits — habit design --------------------------------------------

function TinyHabitDesign({ ctx }: { ctx: RunnerCtx }) {
  const [anchor, setAnchor] = useState("");
  const [behavior, setBehavior] = useState("");
  const [celebration, setCelebration] = useState("");
  const [done, setDone] = useState(false);
  const ready = anchor.trim() && behavior.trim() && celebration.trim();

  if (done) return <CompleteScreen ctx={ctx} />;

  return (
    <RunnerShell
      title="A tiny habit has three parts. Make it ridiculously small — under 30 seconds."
      ctaLabel="Lock it in"
      ctaDisabled={!ready}
      onCtaClick={async () => {
        await ctx.finish({ anchor, behavior, celebration });
        setDone(true);
      }}
      cta={null}
      body={
        <div className="space-y-3">
          <Field
            label="After I…"
            sub="An existing routine that already happens daily (e.g. 'brush my teeth')."
            value={anchor}
            onChange={setAnchor}
            placeholder="brush my teeth"
          />
          <Field
            label="I will…"
            sub="A tiny action under 30 seconds (e.g. 'do one push-up')."
            value={behavior}
            onChange={setBehavior}
            placeholder="do one push-up"
          />
          <Field
            label="Then I'll celebrate by…"
            sub="Something tiny and immediate (e.g. 'say YES! out loud')."
            value={celebration}
            onChange={setCelebration}
            placeholder="say YES out loud"
          />
        </div>
      }
    />
  );
}

// ---- CBT — thought record (4 columns) ---------------------------------------

function ThoughtRecord({ ctx }: { ctx: RunnerCtx }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    situation: "",
    thought: "",
    evidence_for: "",
    evidence_against: "",
    alternative: "",
  });
  const [done, setDone] = useState(false);

  if (done) return <CompleteScreen ctx={ctx} />;

  const steps = [
    { key: "situation", q: "What just happened? (one sentence)", placeholder: "I missed the deadline I committed to." },
    { key: "thought", q: "What's the automatic thought?", placeholder: "I always let people down." },
    { key: "evidence_for", q: "What evidence supports that thought?", placeholder: "I missed this one. Last month I…" },
    { key: "evidence_against", q: "What evidence pushes back?", placeholder: "I delivered the previous three on time, and I let them know early." },
    { key: "alternative", q: "What's an alternative interpretation that's also possibly true?", placeholder: "I missed one deadline. I'm someone who usually delivers and was honest about slipping." },
  ];
  const cur = steps[step]!;

  async function next() {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      await ctx.finish(data);
      setDone(true);
    }
  }

  return (
    <RunnerShell
      title={`Step ${step + 1} of ${steps.length}`}
      ctaLabel={step < steps.length - 1 ? "Next →" : "Finish"}
      ctaDisabled={!data[cur.key as keyof typeof data].trim()}
      onCtaClick={next}
      cta={null}
      body={
        <div className="space-y-2">
          <p className="text-sm font-medium">{cur.q}</p>
          <textarea
            rows={3}
            value={data[cur.key as keyof typeof data]}
            onChange={(e) => setData({ ...data, [cur.key]: e.target.value })}
            placeholder={cur.placeholder}
            className="w-full resize-none rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-neutral-400"
          />
        </div>
      }
    />
  );
}

// ---- Positive Psychology — savoring -----------------------------------------

function Savoring({ ctx }: { ctx: RunnerCtx }) {
  const [moment, setMoment] = useState("");
  const [feeling, setFeeling] = useState("");
  const [whatMattered, setWhatMattered] = useState("");
  const [done, setDone] = useState(false);
  const ready = moment.trim() && feeling.trim() && whatMattered.trim();

  if (done) return <CompleteScreen ctx={ctx} />;

  return (
    <RunnerShell
      ctaLabel="Save"
      ctaDisabled={!ready}
      onCtaClick={async () => {
        await ctx.finish({ moment, feeling, what_mattered: whatMattered });
        setDone(true);
      }}
      cta={null}
      body={
        <div className="space-y-3">
          <Field
            label="One moment from the last day or two that landed well."
            value={moment}
            onChange={setMoment}
            placeholder="Walking home after the meeting and the air felt cool."
          />
          <Field
            label="What did it feel like in your body or chest?"
            value={feeling}
            onChange={setFeeling}
            placeholder="Light, slightly buoyant, like I could breathe deeper."
          />
          <Field
            label="What does that point to about what matters to you?"
            value={whatMattered}
            onChange={setWhatMattered}
            placeholder="I'm at my best when I'm not in performance mode."
          />
        </div>
      }
    />
  );
}

// ---- Positive Psychology — gratitude three ----------------------------------

function GratitudeThree({ ctx }: { ctx: RunnerCtx }) {
  const [items, setItems] = useState(["", "", ""]);
  const [done, setDone] = useState(false);
  const ready = items.every((i) => i.trim().length > 2);

  if (done) return <CompleteScreen ctx={ctx} />;

  return (
    <RunnerShell
      title="Three specific things you're grateful for. Specificity beats quantity — one concrete detail each."
      ctaLabel="Done"
      ctaDisabled={!ready}
      onCtaClick={async () => {
        await ctx.finish({ items });
        setDone(true);
      }}
      cta={null}
      body={
        <div className="space-y-2">
          {items.map((v, i) => (
            <input
              key={i}
              value={v}
              onChange={(e) => {
                const next = [...items];
                next[i] = e.target.value;
                setItems(next);
              }}
              placeholder={
                i === 0
                  ? "e.g. The way M held my hand during the awkward dinner."
                  : i === 1
                    ? "Something you used today (a tool, a person, a place)."
                    : "Something small you almost overlooked."
              }
              className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-neutral-400"
              maxLength={140}
            />
          ))}
        </div>
      }
    />
  );
}

// ---- CBT lite — reframe one --------------------------------------------------

function ReframeOne({ ctx }: { ctx: RunnerCtx }) {
  const [thought, setThought] = useState("");
  const [reframe, setReframe] = useState("");
  const [done, setDone] = useState(false);
  const ready = thought.trim() && reframe.trim();

  if (done) return <CompleteScreen ctx={ctx} />;

  return (
    <RunnerShell
      ctaLabel="Save"
      ctaDisabled={!ready}
      onCtaClick={async () => {
        await ctx.finish({ thought, reframe });
        setDone(true);
      }}
      cta={null}
      body={
        <div className="space-y-3">
          <Field
            label="A thought that's been sticky for you today."
            value={thought}
            onChange={setThought}
            placeholder="I'm falling behind everyone."
            multiline
          />
          <Field
            label="One alternative interpretation that's also possibly true."
            sub="Not 'positive thinking.' Just another lens that might also fit the facts."
            value={reframe}
            onChange={setReframe}
            placeholder="I'm on a different timeline. The comparison is to a fiction."
            multiline
          />
        </div>
      }
    />
  );
}

// ---- Fallback for templates without a custom UI -----------------------------

function FallbackPractice({ ctx }: { ctx: RunnerCtx }) {
  const [text, setText] = useState("");
  const [done, setDone] = useState(false);

  if (done) return <CompleteScreen ctx={ctx} />;

  return (
    <RunnerShell
      ctaLabel="Done"
      ctaDisabled={text.trim().length < 4}
      onCtaClick={async () => {
        await ctx.finish({ note: text });
        setDone(true);
      }}
      cta={null}
      body={
        <textarea
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Write whatever surfaces."
          className="w-full resize-none rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-neutral-400"
        />
      }
    />
  );
}

// ---- Field helper -----------------------------------------------------------

function Field(props: {
  label: string;
  sub?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  multiline?: boolean;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-neutral-700">{props.label}</label>
      {props.sub && <p className="mb-1 text-[11px] text-neutral-500">{props.sub}</p>}
      {props.multiline ? (
        <textarea
          rows={2}
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          placeholder={props.placeholder}
          className="w-full resize-none rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-neutral-400"
        />
      ) : (
        <input
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          placeholder={props.placeholder}
          className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-neutral-400"
        />
      )}
    </div>
  );
}
